import type { HTMLAttributes, JSX, ReactNode } from 'react';
import { useEffect } from 'react';
import classNames from 'classnames';
import { motion, useReducedMotion } from 'motion/react';
import { Box, Loader, Text, Title } from '@mantine/core';
import { useDeviceType } from '../../hooks';
import { staggerContainerVariants } from '../../motion';
import classes from './Page.module.css';

interface IPageProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode[];
  isLoading?: boolean;
  footer?: ReactNode;
  /** Always stack section children in a column (e.g. Settings), not a wrapping row. */
  stack?: boolean;
  /** Constrain content to a left-aligned reading column (~52rem, flush with the
   *  page title) instead of full width. */
  narrow?: boolean;
  /** Remove the content cap so dense multi-column tables (e.g. the master plan grid) use the whole page width. */
  wide?: boolean;
}

function Page({
  title,
  subtitle,
  children,
  actions,
  isLoading,
  footer,
  stack,
  narrow,
  wide,
}: Readonly<IPageProps>): JSX.Element {
  useEffect(() => {
    document.title = `${title} | Grace Bible Church Gainesville`;
  }, [title]);

  const { isMobile } = useDeviceType();
  const prefersReducedMotion = useReducedMotion();
  const useColumn = stack || isMobile;

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <Loader size="xl" />
      </Box>
    );
  }

  const childrenClassName = classNames(classes.childrenContainer, {
    [classes.childrenColumn]: useColumn,
    [classes.childrenRow]: !useColumn,
    [classes.stack]: stack,
  });

  return (
    <Box
      component="main"
      className={classNames(classes.container, { [classes.narrow]: narrow, [classes.wide]: wide })}
    >
      <Title order={2} ta="left" className={classes.pageTitle}>
        {title}
      </Title>
      {subtitle && (
        <Text size="sm" className={classes.pageSubtitle}>
          {subtitle}
        </Text>
      )}
      {actions && <Box className={classes.pageActions}>{actions}</Box>}
      {prefersReducedMotion ? (
        <div className={childrenClassName}>{children}</div>
      ) : (
        <motion.div
          className={childrenClassName}
          variants={staggerContainerVariants}
          initial="hidden"
          animate="visible"
        >
          {children}
        </motion.div>
      )}
      {footer}
    </Box>
  );
}

export default Page;
